import React from 'react';
import get from 'lodash/get';
import has from 'lodash/has';
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  MenuItem,
  TextField,
  Theme,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colorPalette } from '@qualifio/ui-commons';
import { defaultCurrencyImage } from '@loyalty/shared-common';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { makeStyles } from '@mui/styles';
import { BackgroundImageField } from './BackgroundImageField';
import { FieldProps } from './FieldProps';
import { FontsSelect } from './FontsSelect';
import { UploadableImage } from '../../../../shared/image';
import { useProgram } from '../../../../loyalty/ProgramContext';

const useStyles = makeStyles(({ spacing }: Theme) => ({
  field: {
    marginBottom: spacing(0),
  },
}));

export function Field(props: FieldProps): React.ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { name, type, endAdornment, onApply, size, options, ...rest } = props;
  const classes = useStyles();
  const {
    control,
    formState: { errors },
    watch,
    setError,
    clearErrors,
  } = useFormContext();
  const { program } = useProgram();

  const hasErrors = (path: string) => has(errors, path);
  const getErrors = (path: string) => get(errors, `${path}.message`) as unknown as string;
  const fieldValue = watch(name);

  const handleError = (fieldName: string, message?: string) => {
    if (message) {
      setError(fieldName, { message });
    } else {
      clearErrors(fieldName);
    }
  };

  return name.includes('fontFamily') ? (
    <FontsSelect {...props} />
  ) : type === 'backgroundImage' ? (
    <BackgroundImageField {...props} />
  ) : type === 'image' ? (
    <Controller
      control={control}
      name={name}
      render={({ field: { ref, onChange, value, ...field }, fieldState: { error } }) => (
        <FormControl style={{ marginBottom: '0px' }} error={!!error}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 1 }}>
            <FormLabel>{rest.label}</FormLabel>
            {value && (
              <IconButton
                size="small"
                onClick={() => {
                  onChange({ target: { name, value: '' } });
                  clearErrors(name);
                  onApply?.();
                }}
                sx={{
                  color: colorPalette.red.main,
                  '&:hover': {
                    backgroundColor: colorPalette.red.light,
                    color: colorPalette.red.dark,
                  },
                }}
                aria-label="Remove image"
              >
                <FontAwesomeIcon icon={faTimes} />
              </IconButton>
            )}
          </Box>
          <UploadableImage
            {...field}
            value={value}
            programId={program.id}
            type={name}
            errorMessage={undefined}
            size={name === 'favicon' ? 64 : 175}
            onChange={event => {
              onChange(event);
              clearErrors(name);
              onApply?.();
            }}
            onError={handleError}
          >
            <img src={defaultCurrencyImage} />
          </UploadableImage>
          {error?.message && (
            <FormHelperText error>
              <span>{error.message}</span>
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  ) : (
    <Controller
      control={control}
      name={name}
      render={({ field: { ref, onBlur, onChange, ...field }, fieldState: { error } }) =>
        type === 'select' ? (
          <FormControl style={{ marginBottom: '0px' }}>
            <FormLabel>{rest.label}</FormLabel>
            <TextField
              select
              className={classes.field}
              error={!!error}
              helperText={error?.message}
              inputRef={ref}
              onBlur={onBlur}
              onChange={event => {
                onChange(event);
                onApply?.();
              }}
              InputProps={{
                endAdornment: endAdornment?.(fieldValue),
              }}
              {...field}
              {...rest}
              label=""
            >
              {options?.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.text}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        ) : (
          <FormControl style={{ marginBottom: '0px' }}>
            <FormLabel>{rest.label}</FormLabel>
            <TextField
              className={classes.field}
              error={hasErrors(name)}
              helperText={getErrors(name)}
              inputRef={ref}
              onChange={onChange}
              onBlur={() => {
                onBlur();
                onApply?.();
              }}
              {...field}
              InputProps={{
                endAdornment: endAdornment?.(fieldValue),
              }}
              {...rest}
              label=""
            />
          </FormControl>
        )
      }
    />
  );
}
